/**
 * Share Chat helpers — URL/token parsing + clipboard (no storage, no auth decisions).
 */

/**
 * Sync clipboard write via the `copy` event.
 * Success only when clipboardData.setData actually ran — never trust
 * document.execCommand("copy")'s boolean alone (often true on production
 * without writing anything).
 */
export function copyTextWithCopyEvent(text) {
  const value = text ?? "";
  if (!value || typeof document === "undefined") return false;

  let wroteToClipboard = false;
  const onCopy = (event) => {
    try {
      if (!event.clipboardData) return;
      event.clipboardData.setData("text/plain", value);
      event.preventDefault();
      wroteToClipboard = true;
    } catch (error) {
      console.warn("clipboardData.setData failed:", error);
    }
  };

  document.addEventListener("copy", onCopy, true);

  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.setAttribute("readonly", "");
  textArea.setAttribute("aria-hidden", "true");
  textArea.style.cssText =
    "position:fixed;top:0;left:0;width:1px;height:1px;padding:0;margin:0;border:0;outline:none;opacity:0;";
  document.body.appendChild(textArea);

  const selection = document.getSelection();
  const previouslySelected =
    selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

  try {
    textArea.focus({ preventScroll: true });
    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);
    document.execCommand("copy");
  } catch (error) {
    console.warn("execCommand copy failed:", error);
  } finally {
    document.removeEventListener("copy", onCopy, true);
    document.body.removeChild(textArea);
    if (previouslySelected && selection) {
      selection.removeAllRanges();
      selection.addRange(previouslySelected);
    } else if (selection) {
      selection.removeAllRanges();
    }
  }

  return wroteToClipboard;
}

function canUseClipboardApi() {
  return (
    typeof window !== "undefined" &&
    !!window.isSecureContext &&
    typeof navigator !== "undefined" &&
    !!navigator.clipboard &&
    typeof navigator.clipboard.writeText === "function"
  );
}

/**
 * Copy text for share links. Prefers verified sync write (user-gesture safe),
 * then Clipboard API. Returns true only when a write path succeeded.
 */
export async function copyTextToClipboard(text) {
  const value = text == null ? "" : String(text);
  if (!value) return false;

  // Must run first while still in the click turn when possible.
  const syncOk = copyTextWithCopyEvent(value);
  if (syncOk) {
    if (canUseClipboardApi()) {
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        // Sync write already confirmed — ignore Clipboard API errors.
      }
    }
    return true;
  }

  if (canUseClipboardApi()) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (clipboardError) {
      console.warn("clipboard.writeText failed:", clipboardError);
      return false;
    }
  }

  return false;
}

/**
 * Extract share token from backend shareUrl or a full/relative path.
 * Supports:
 *  - /api/shared/{token}
 *  - /shared/{token}
 *  - /share/{token}
 *  - https://host/.../share/{token}
 */
export function extractShareToken(shareUrl) {
  if (shareUrl == null) return null;
  const raw = String(shareUrl).trim();
  if (!raw) return null;

  const patterns = [
    /\/api\/shared\/([^/?#]+)/i,
    /\/shared\/([^/?#]+)/i,
    /\/share\/([^/?#]+)/i,
  ];

  for (const pattern of patterns) {
    const match = raw.match(pattern);
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1]);
      } catch {
        return match[1];
      }
    }
  }

  // Bare token (no slashes)
  if (!raw.includes("/") && !raw.includes("?") && !raw.includes("#")) {
    return raw;
  }

  return null;
}

/**
 * Build the public frontend share URL that users can open in the browser.
 * Backend may return an API path; we always expose /share/:token.
 */
export function buildPublicShareUrl(tokenOrShareUrl) {
  const token = extractShareToken(tokenOrShareUrl) || String(tokenOrShareUrl || "").trim();
  if (!token) return null;

  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  return `${origin}/share/${encodeURIComponent(token)}`;
}

/**
 * Map HTTP / API errors to UI status codes for the shared chat page.
 */
export function mapSharedChatError(error) {
  const status = error?.status ?? error?.response?.status ?? null;
  const code = String(
    error?.code || error?.errorCode || error?.body?.code || "",
  ).toLowerCase();
  const message = String(
    error?.message || error?.body?.message || error?.body?.detail || "",
  ).toLowerCase();

  if (
    status === 410 ||
    code.includes("expir") ||
    message.includes("expir")
  ) {
    return "expired";
  }

  if (
    status === 404 ||
    status === 403 ||
    code.includes("revok") ||
    code.includes("invalid") ||
    message.includes("revok") ||
    message.includes("not found") ||
    message.includes("no longer") ||
    message.includes("unavailable")
  ) {
    return "unavailable";
  }

  return "error";
}
